import { api, type Page } from "@/lib/api/client";
import type { paths } from "@/lib/api/generated/api";

/** `GET /hr/employees/:id/account` — the linked account, or `{ account: null }` (17 Ekim 2026). */
export type EmployeeAccount =
  paths["/api/v1/hr/employees/{id}/account"]["get"]["responses"][200]["content"]["application/json"] extends {
    data: infer T;
  }
    ? T
    : never;
import type {
  CancelExitInput,
  Employee,
  EmployeeAddress,
  EmployeeContact,
  EmployeeFilters,
  EmployeeListItem,
  EmployeeSensitive,
  Employment,
  EmploymentAssignment,
  EmploymentUpdateInput,
  ExitReadiness,
  StartEmploymentInput,
  TerminateInput,
} from "./employees.types";

const BASE = "/hr/employees";

export const employeesApi = {
  /**
   * The personnel table. With `legalEntityId`, each row carries `currentEmployment` — that
   * company's running contract — where the caller's `hr.employment.read` reaches it (H5).
   */
  list(filters: EmployeeFilters): Promise<Page<EmployeeListItem>> {
    return api.page<EmployeeListItem>(BASE, { params: { ...filters } });
  },

  get(id: string): Promise<Employee> {
    return api.get<Employee>(`${BASE}/${id}`);
  },

  /** The account linked to this employee — one at most («bir çalışan, tek hesap»). */
  account(id: string): Promise<EmployeeAccount> {
    return api.get<EmployeeAccount>(`${BASE}/${id}/account`);
  },

  /**
   * Switches the linked account off or on, with a reason. Off: the person cannot sign in and every
   * open session ends at once. The answer is the account as it now is.
   */
  setAccountActive(id: string, active: boolean, reason: string): Promise<EmployeeAccount> {
    return api.post<EmployeeAccount>(`${BASE}/${id}/account/${active ? "reactivate" : "deactivate"}`, {
      reason,
    });
  },

  /**
   * The audited read of a person's identity number and contacts.
   *
   * GEREKÇE İSTEĞE BAĞLI (31 Ekim 2026, kullanıcı: «erişim gerekçesi zorunlu olamaz; hatta istemesin»):
   * ekran sormuyor, sunucu okumayı yine her seferinde kayda yazıyor («Gerekçe istenmedi»). Bir gerekçe
   * verilirse GÖVDEDE gidiyor (İK-25 — adres satırında ters vekil günlüğüne düşüyordu).
   */
  sensitive(id: string, reason?: string): Promise<EmployeeSensitive> {
    // Bir OKUMA (sunucu kayda geçiriyor): yazma sayılıp ekranı tazelemesin — kart açılınca kendiliğinden gidiyor.
    return api.post<EmployeeSensitive>(`${BASE}/${id}/sensitive`, reason ? { reason } : {}, { quiet: true });
  },

  addresses(id: string): Promise<EmployeeAddress[]> {
    return api.get<EmployeeAddress[]>(`${BASE}/${id}/addresses`);
  },

  contacts(id: string): Promise<EmployeeContact[]> {
    return api.get<EmployeeContact[]>(`${BASE}/${id}/contacts`);
  },

  /**
   * Employment records, filtered.
   *
   * Every property is `| undefined` by CONVENTION in this layer: with
   * `exactOptionalPropertyTypes` on, a caller holding a value that may not exist yet — an id
   * read from the URL — otherwise has to spread it in conditionally at every call site to
   * express "I do not have this". Widening the parameter type is the readable half of that
   * trade; the query builder drops undefined values before they reach the wire.
   */
  employments(filters: {
    employeeId?: string | undefined;
    legalEntityId?: string | undefined;
    status?: string | undefined;
    page?: number | undefined;
    limit?: number | undefined;
    sortBy?: string | undefined;
    sortOrder?: "asc" | "desc" | undefined;
    q?: string | undefined;
    /** Bugün bu birimde görevli olanlar — sunucu görevlendirmeye uzanarak süzüyor. */
    organizationUnitId?: string | undefined;
    /** ik-yasam-dongusu 19 Ekim 2026 (İK-03): planlanmış (yürürlüğe girmemiş) çıkışlar. */
    exitPlanned?: boolean | undefined;
  }): Promise<Page<Employment>> {
    return api.page<Employment>("/hr/employments", { params: { ...filters } });
  },

  /**
   * Tek istihdam kaydı — kartın «Yönetici» satırı için (görevlendirme yöneticinin İSTİHDAM
   * kimliğini taşıyor, adını değil; ad bu kaydın `employee` referansında).
   */
  employment(id: string): Promise<Employment> {
    return api.get<Employment>(`/hr/employments/${encodeURIComponent(id)}`);
  },

  /**
   * ÇIKIŞ HAZIRLIĞI — «bu kişinin çıkışı bugün kapatılabilir mi» sorusunun SUNUCU cevabı.
   *
   * Ekran zimmeti ve evrakı ayrıca listeliyor ama kararı kendisi VERMİYOR: aynı kuralları
   * ikinci kez yazmak, ekranın «kapatabilirsin» dediği bir çıkışın uçta reddedilmesi
   * demekti.
   */
  exitReadiness(employmentId: string): Promise<ExitReadiness> {
    return api.get<ExitReadiness>(`/hr/employments/${employmentId}/exit-readiness`);
  },

  /**
   * Çıkışı kaydeder. `overrideReason` yalnız engel varken gerekiyor ve denetime yazılıyor.
   *
   * ik-yasam-dongusu 19 Ekim 2026 (İK-03): bugünkü ya da geçmiş tarih ÇIKIŞI HEMEN yürürlüğe
   * sokuyor (durum `ENDED`); ileri tarih çıkışı PLANLIYOR — durum değişmiyor, `endDate` yazılıyor
   * ve sunucunun saatlik işi o gün kapatıyor.
   */
  terminate(employmentId: string, input: TerminateInput): Promise<Employment> {
    return api.post<Employment>(`/hr/employments/${employmentId}/terminate`, input);
  },

  /** ik-yasam-dongusu 19 Ekim 2026 (İK-03): planlanan çıkışı tarihinden önce gerekçeyle geri alır. */
  cancelExit(employmentId: string, input: CancelExitInput): Promise<Employment> {
    return api.post<Employment>(`/hr/employments/${encodeURIComponent(employmentId)}/cancel-exit`, input);
  },

  /**
   * ik-yasam-dongusu 19 Ekim 2026 (İK-05): «İşe başlat» — `PLANNED` → `ACTIVE`, başlangıç günü
   * geldiyse. `PATCH` artık durum taşımıyor; bir sözleşme yalnız buradan başlıyor.
   */
  startEmployment(employmentId: string, input: StartEmploymentInput): Promise<Employment> {
    return api.post<Employment>(`/hr/employments/${encodeURIComponent(employmentId)}/start`, input);
  },

  /**
   * Sözleşmenin kendi alanları — H6 (kurumsal e-posta/telefon) ve H8 (deneme süresi bitişi).
   * `version` zorunlu: iki kişi aynı kaydı düzenlerken biri ötekinin yazdığını ezmesin.
   * `null` değeri temizler; gönderilmeyen alan değişmez.
   */
  updateEmployment(employmentId: string, input: EmploymentUpdateInput): Promise<Employment> {
    return api.patch<Employment>(`/hr/employments/${employmentId}`, input);
  },

  /** Bir tarihte yürürlükteki görevlendirme(ler) — kartın «Birim» satırı için. */
  assignments(
    employmentId: string,
    filters: { at?: string | undefined; isPrimary?: boolean | undefined; limit?: number | undefined },
  ): Promise<Page<EmploymentAssignment>> {
    return api.page<EmploymentAssignment>(`/hr/employments/${employmentId}/assignments`, {
      params: { ...filters },
    });
  },
};
